import React from 'react';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useAuthStore } from '../stores/authStore';
import { Dialog } from './shared/Dialog';

interface LoginRequiredDialogProps {
  isOpen: boolean;
  onClose: () => void;
  feature: 'logo' | 'mollie' | 'email';
}

export const LoginRequiredDialog: React.FC<LoginRequiredDialogProps> = ({
  isOpen,
  onClose,
  feature
}) => {
  const { t } = useTranslation();
  const openLoginModal = useAuthStore((state) => state.openLoginModal);

  const getFeatureContent = () => {
    switch (feature) {
      case 'logo':
        return {
          title: t('auth.loginRequired.logo.title'),
          description: t('auth.loginRequired.logo.description')
        };
      case 'mollie':
        return {
          title: t('auth.loginRequired.mollie.title'),
          description: t('auth.loginRequired.mollie.description')
        };
      case 'email':
        return {
          title: t('auth.loginRequired.email.title'),
          description: t('auth.loginRequired.email.description')
        };
    }
  };

  const content = getFeatureContent();

  const handleSignIn = () => {
    onClose();
    openLoginModal();
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} size="md">
      <div className="bg-white rounded-3xl border shadow-lg overflow-hidden">
        <div className="px-8 py-10 text-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6"
          >
            <Lock className="w-6 h-6 text-gray-600" />
          </motion.div>

          <motion.h2 
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="text-2xl font-semibold text-gray-900 mb-3"
          >
            {content.title}
          </motion.h2>

          <motion.p 
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.3 }}
            className="text-gray-600 text-lg mb-8"
          >
            {content.description}
          </motion.p>

          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.3 }}
            onClick={handleSignIn}
            className="btn-primary"
          >
            {t('auth.signInToContinue')}
          </motion.button>

          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.3 }}
            className="text-gray-500 text-sm mt-4"
          >
            {t('auth.freeAccount')}
          </motion.p>
        </div>
      </div>
    </Dialog>
  );
}; 